import "@/App.css";
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "./lib/api";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { CartProvider } from "./context/CartContext";
import { CustomerProvider } from "./context/CustomerContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { BottomNav } from "./components/BottomNav";

import Home from "./pages/Home";
import Catalog from "./pages/Catalog";
import CategoryPage from "./pages/CategoryPage";
import ProductConfigure from "./pages/ProductConfigure";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import CaraPesan from "./pages/CaraPesan";
import Kontak from "./pages/Kontak";
import CustomerAccount from "./pages/CustomerAccount";
import GuestTrackOrder from "./pages/GuestTrackOrder";
import InvoiceClaim from "./pages/InvoiceClaim";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminOrderDetail from "./pages/admin/AdminOrderDetail";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminInvoiceForm from "./pages/admin/AdminInvoiceForm";
import AdminInvoiceDetail from "./pages/admin/AdminInvoiceDetail";
import AdminInvoicePrint from "./pages/admin/AdminInvoicePrint";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminProductEdit from "./pages/admin/AdminProductEdit";
import AdminCustomRequests from "./pages/admin/AdminCustomRequests";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminFinance from "./pages/admin/AdminFinance";
import AdminFinanceStats from "./pages/admin/AdminFinanceStats";
import AdminWorkerPendingWages from "./pages/admin/AdminWorkerPendingWages";
import AdminDeliveryWageSettings from "./pages/admin/AdminDeliveryWageSettings";
import AdminWageSettings from "./pages/admin/AdminWageSettings";
import AdminLogistics from "./pages/admin/AdminLogistics";
import AdminExport from "./pages/admin/AdminExport";
import AdminAdmins from "./pages/admin/AdminAdmins";
import AdminDiscounts from "./pages/admin/AdminDiscounts";
import AdminReferrals from "./pages/admin/AdminReferrals";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminMaterials from "./pages/admin/AdminMaterials";
import CustomOrderPage from "./pages/CustomOrderPage";

function QueryRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search || ""}`} replace />;
}

function CustomerShell({ children }) {
  const [store, setStore] = useState(null);
  const location = useLocation();
  useEffect(() => { api.get("/store-info").then((r) => setStore(r.data)).catch(() => {}); }, []);

  const isConfigurator =
    location.pathname.startsWith("/produk/") &&
    !location.pathname.startsWith("/produk/kategori/");
  const isCheckout = location.pathname === "/checkout";
  const hideBottomNav = isConfigurator || isCheckout;

  return (
    <div className={`flex min-h-screen flex-col ${hideBottomNav ? "" : "pb-16 md:pb-0"}`}>
      <Header storeInfo={store} />
      <div className="flex-1">{children}</div>
      <Footer storeInfo={store} />
      {!hideBottomNav && <BottomNav />}
    </div>
  );
}

function Layout() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isTeamHost = window.location.hostname === "team.sogilfurniture.com";
  const isPwaAdmin = searchParams.get("pwa") === "admin" || window.location.search.indexOf("pwa=admin") !== -1;
  const isAdmin = isTeamHost || isPwaAdmin || location.pathname.startsWith("/admin");

  useEffect(() => {
    const link = document.getElementById("manifest-link");
    const themeMeta = document.getElementById("theme-color-meta") || document.querySelector('meta[name="theme-color"]');
    const appleIcon = document.getElementById("apple-icon-link") || document.querySelector('link[rel="apple-touch-icon"]');
    const favicon = document.getElementById("favicon-link") || document.querySelector('link[rel="icon"]');
    const appleTitle = document.getElementById("apple-title-meta") || document.querySelector('meta[name="apple-mobile-web-app-title"]');

    if (isAdmin) {
      if (link) link.setAttribute("href", "/manifest-admin.json");
      if (themeMeta) themeMeta.setAttribute("content", "#2C1E16");
      if (appleIcon) appleIcon.setAttribute("href", "/icons/apple-touch-icon-admin.png");
      if (favicon) favicon.setAttribute("href", "/icons/icon-admin-192.png");
      if (appleTitle) appleTitle.setAttribute("content", "Sogil Team");
    } else {
      if (link) link.setAttribute("href", "/manifest.json");
      if (themeMeta) themeMeta.setAttribute("content", "#8B5A2B");
      if (appleIcon) appleIcon.setAttribute("href", "/icons/apple-touch-icon.png");
      if (favicon) favicon.setAttribute("href", "/icons/icon-192.png");
      if (appleTitle) appleTitle.setAttribute("content", "Sogil Furniture");
    }
  }, [isAdmin]);

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/admin/orders" replace />} />
        <Route path="/login" element={<Navigate to="/admin/login" replace />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/invoices/:id/print" element={<AdminInvoicePrint />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/orders" replace />} />
          <Route path="overview" element={<Navigate to="/admin/orders" replace />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="orders/:id" element={<AdminOrderDetail />} />
          <Route path="logistics" element={<AdminLogistics />} />
          <Route path="invoices" element={<AdminInvoices />} />
          <Route path="invoices/new" element={<AdminInvoiceForm />} />
          <Route path="invoices/:id" element={<AdminInvoiceDetail />} />
          <Route path="invoices/:id/edit" element={<AdminInvoiceForm />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="products/:id" element={<AdminProductEdit />} />
          <Route path="materials" element={<AdminMaterials />} />
          <Route path="categories" element={<Navigate to="/admin/products" replace />} />
          <Route path="custom-requests" element={<AdminCustomRequests />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="finance" element={<AdminFinance />} />
          <Route path="finance/statistik" element={<AdminFinanceStats />} />
          <Route path="finance/statistics" element={<QueryRedirect to="/admin/finance/statistik" />} />
          <Route path="statistik" element={<QueryRedirect to="/admin/finance/statistik" />} />
          <Route path="pending-payments" element={<AdminWorkerPendingWages />} />
          <Route path="wages/pending" element={<Navigate to="/admin/pending-payments" replace />} />
          <Route path="wages" element={<Navigate to="/admin/pending-payments" replace />} />
          <Route path="wage-settings" element={<AdminWageSettings />} />
          <Route path="delivery-wage-settings" element={<AdminDeliveryWageSettings />} />
          <Route path="export" element={<AdminExport />} />
          <Route path="discounts" element={<AdminDiscounts />} />
          <Route path="referrals" element={<AdminReferrals />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="admins" element={<AdminAdmins />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
        {isTeamHost && <Route path="*" element={<Navigate to="/admin/orders" replace />} />}
      </Routes>
    );
  }
  return (
    <CustomerShell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/produk" element={<Catalog />} />

        <Route
          path="/produk/kategori/:category"
          element={<CategoryPage />}
        />
        
        <Route path="/produk/:slug" element={<ProductConfigure />} />
        <Route path="/request-custom" element={<CustomOrderPage />} />
        <Route path="/custom-order" element={<CustomOrderPage />} />
        <Route path="/keranjang" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/pesanan/:id" element={<OrderConfirmation />} />
        <Route path="/cara-pesan" element={<CaraPesan />} />
        <Route path="/kontak" element={<Kontak />} />
        <Route path="/akun" element={<CustomerAccount />} />
        <Route path="/klaim-invoice" element={<InvoiceClaim />} />
        <Route path="/klaim" element={<Navigate to="/klaim-invoice" replace />} />
        <Route path="/lacak" element={<GuestTrackOrder />} />
      </Routes>
    </CustomerShell>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  return null;
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <CustomerProvider>
              <BrowserRouter>
                <ScrollToTop />
                <Layout />
                <Toaster position="top-center" richColors />
              </BrowserRouter>
            </CustomerProvider>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
